import type { IconSize } from '@amelio/ui-library';

/**
 * How tightly the employee objectives block is drawn. `default` is the block's own scale, the one
 * the profile's Objectives tab reads it at; `compact` is the tighter reading the manager's
 * evaluation page asks for, where the block is one collapsible section among several others.
 *
 * Same vocabulary as the library's `PageHeader` and rich-text toolbar, and the same rule as there:
 * the default is the roomy scale, and only a caller that wants less room says so.
 */
export type ObjectivesOverviewDensity = 'default' | 'compact';

/** Everything the density decides, and nothing else: the invariant classes stay with the markup. */
interface ObjectivesOverviewScale {
    /** The overall progression band. */
    progression: {
        title: string;
        legend: string;
        /** Height of the bar's track. */
        track: string;
        /** Worn by the track and its fill alike, so the bar lifts off the page as one shape. */
        barShadow: string;
        value: string;
    };
    /** The headline tiles. */
    tiles: {
        /**
         * The grid, which at the default scale also draws the frame around the tiles: padding
         * and a grid on one box lay them out exactly as a padded card wrapping a bare grid did,
         * with one element fewer.
         */
        grid: string;
        tile: string;
        title: string;
        iconBox: string;
        iconSize: IconSize;
        value: string;
    };
}

/** The tiles' half of a scale: the row resolves the density once and hands it to each tile. */
export type ObjectivesTilesScale = ObjectivesOverviewScale['tiles'];

/**
 * The block's two scales. A screen chooses between them with the one `density` it passes, so
 * moving a screen from one to the other is that line, and changing a scale is one line here.
 *
 * The default scale keeps two pixel values off the type ladder -- a 40px figure and the 25px line
 * box under a 20px caption -- because it is the appearance the profile tab is measured against;
 * rounding them to a step would change what the tab renders, which is the opposite of the point.
 */
export const OBJECTIVES_OVERVIEW_SCALE: Record<ObjectivesOverviewDensity, ObjectivesOverviewScale> =
    {
        default: {
            progression: {
                title: 'text-xl leading-[25px]',
                legend: 'text-sm',
                track: 'h-5',
                barShadow: 'shadow-sm',
                value: 'text-[40px]',
            },
            tiles: {
                grid: 'gap-4 rounded-xl border border-white bg-white/45 px-4 py-6 md:gap-6 md:px-12 md:py-12',
                tile: 'min-h-48 gap-4 rounded-2xl p-6',
                title: 'text-xl leading-[25px]',
                iconBox: 'size-10',
                iconSize: 'md',
                value: 'text-[40px]',
            },
        },
        compact: {
            progression: {
                title: 'text-xs',
                legend: 'text-xs',
                track: 'h-2',
                barShadow: '',
                value: 'text-lg',
            },
            tiles: {
                grid: 'gap-3',
                tile: 'gap-2 rounded-xl p-4',
                title: 'text-xs',
                iconBox: 'size-7',
                iconSize: 'sm',
                value: 'text-2xl',
            },
        },
    };
